import { formatDay, LICENCE_BODY_LABELS, parseIsoDay, type LicenceBody } from '@wodi/domain';
import type { LicenceStatusView } from '@wodi/shared';
import { Button, Notice, Tag } from '@wodi/ui';

import { ApiError } from '../api/client.ts';
import { licenceStateWords } from '../routes/settings-practitioners.tsx';

// Words and tags shared by the staff screens (docs/plans/phase2-staff-records.md §6).
// DESIGN.md §7: states are words with shapes; no status colours stand alone.

/** `22 Sep 2026`, or "Not recorded". */
export function dayText(iso: string | null): string {
  if (iso === null) return 'Not recorded';
  const parsed = parseIsoDay(iso);
  return parsed.ok ? formatDay(parsed.value) : iso;
}

/** On the staff screens, someone without a practitioner profile holds no licence here. */
export function staffLicenceWords(licence: LicenceStatusView): string {
  return licence.state === 'no_profile' ? 'No licence recorded' : licenceStateWords(licence);
}

export function StaffLicenceTag({ licence }: { licence: LicenceStatusView }) {
  return (
    <Tag
      state={
        licence.state === 'valid'
          ? 'done'
          : licence.state === 'expired'
            ? 'failed'
            : licence.state === 'no_profile'
              ? 'neutral'
              : 'attention'
      }
    >
      {staffLicenceWords(licence)}
    </Tag>
  );
}

export function councilText(body: LicenceBody | null): string {
  return body === null ? 'Not recorded' : LICENCE_BODY_LABELS[body];
}

/** "NCK licence", or "Licence" when the council is unknown or other. */
export function licenceNoun(body: LicenceBody | null): string {
  return body === null || body === 'other' ? 'Licence' : `${LICENCE_BODY_LABELS[body]} licence`;
}

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function LoadFailed({
  error,
  fallback,
  onRetry,
}: {
  error: unknown;
  fallback: string;
  onRetry: () => void;
}) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={onRetry}>
          Try again
        </Button>
      }
    >
      {errorText(error, fallback)}
    </Notice>
  );
}

export function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
